// Node tags: free-text labels on a node (backend: api/routes/node_tags.py). Pure helpers for
// the add control, the tag filter and the map dimming; no imports so the test runner can
// compile it alone.

export const MAX_TAG_LENGTH = 24;
export const MAX_TAGS_PER_NODE = 10;

/** The comparison form of a tag: trimmed, Unicode NFC, lower-cased. The server folds the same way
 * (ingest/node_tags.py fold), so a tag the UI groups is a tag the server treats as one. */
export function tagKey(tag: string): string {
  return tag.trim().normalize("NFC").toLowerCase();
}

// Control, format (zero-width, bidi marks and overrides, BOM), surrogate, line and paragraph separators.
const BLOCKED_CHARS = /[\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}]/u;

/**
 * Checks typed tag text before sending: { tag } to send, { tag: null, error: null } when empty,
 * { tag: null, error: label } when refused. Duplicates and the per-node limit are the server's
 * call (it answers added:false or a 409).
 */
export function checkNewTag(raw: string): { tag: string } | { tag: null; error: string | null } {
  const tag = raw.trim();
  if (tag === "") return { tag: null, error: null };
  if ([...tag].length > MAX_TAG_LENGTH) return { tag: null, error: "Too Long" };
  if (tag === "." || tag === ".." || BLOCKED_CHARS.test(tag)) return { tag: null, error: "Not Allowed" };
  return { tag };
}

const ERROR_LABELS: Record<string, string> = {
  tag_empty: "Empty Tag",
  tag_too_long: "Too Long",
  tag_invalid: "Not Allowed",
  tag_limit: "Tag Limit Reached",
  node_retired: "Node Retired",
  node_not_found: "Node Not Found",
  tag_not_found: "Tag Not Found",
};

/** Short Title Case label for an API error message (the server's `detail` is a short code). */
export function tagErrorLabel(message: string): string {
  const m = /\u2014 ([a-z_]+)$/.exec(message);
  return (m && ERROR_LABELS[m[1]]) || "Failed";
}

/**
 * Filter by several tags at once: a node matches when it has ANY of the selected tags.
 * An empty selection matches every node.
 */
export function nodeMatchesTags(nodeTags: readonly string[] | undefined | null, selectedKeys: readonly string[]): boolean {
  if (selectedKeys.length === 0) return true;
  if (!nodeTags || nodeTags.length === 0) return false;
  const have = new Set(nodeTags.map(tagKey));
  return selectedKeys.some((k) => have.has(k));
}

/** Map dimming: a node is dimmed (never hidden) when a tag filter is on and it does not match. */
export function dimmedByTags(nodeTags: readonly string[] | undefined | null, selectedKeys: readonly string[]): boolean {
  return !nodeMatchesTags(nodeTags, selectedKeys);
}

export interface TagCount {
  key: string;
  tag: string;
  count: number;
}

/** Every distinct tag (case-insensitive) with its node count, sorted by name. */
export function collectTags(nodes: Iterable<{ tags?: readonly string[] | null }>): TagCount[] {
  const byKey = new Map<string, TagCount>();
  for (const n of nodes) {
    for (const tag of n.tags ?? []) {
      const key = tagKey(tag);
      const hit = byKey.get(key);
      if (hit) hit.count += 1;
      else byKey.set(key, { key, tag, count: 1 });
    }
  }
  return [...byKey.values()].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

/** Adds the key to the selection when absent, removes it when present. */
export function toggleTagKey(selected: readonly string[], key: string): string[] {
  return selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key];
}

/** The selection with dead keys dropped, as the SAME array when nothing was dropped (a stable
 * identity, so nothing keyed on it re-runs on every live node update). */
export function keepSelection(selected: string[], available: readonly TagCount[]): string[] {
  const kept = pruneSelection(selected, available);
  return kept.length === selected.length ? selected : kept;
}

/** Drops selected keys that no node carries any more (a removed tag must not leave a dead filter). */
export function pruneSelection(selected: readonly string[], available: readonly TagCount[]): string[] {
  const live = new Set(available.map((t) => t.key));
  return selected.filter((k) => live.has(k));
}
